import { isFunction, validateNotNullOrUndefined } from '@vy/utils';
import type { PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext } from 'react';
import type { URLSearchParamsInit } from 'react-router-dom';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import { useVyNavigate } from '../hooks/use-vy-navigate';

const useVySearchParams = (defaultParams?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] => {
  const [searchParams] = useSearchParams(defaultParams);
  const navigate = useVyNavigate();

  const handleRouteChangeCallback = useCallback<SetURLSearchParams>(
    (newParams) => {
      const searchUri = isFunction(newParams) ? newParams(searchParams) : newParams;
      const search = searchUri ? `?${searchUri.toString()}` : '';

      navigate({
        pathname: window.location.pathname,
        search,
      });
    },
    [searchParams, navigate],
  );

  return [searchParams, handleRouteChangeCallback];
};

export const GlobalSearchParamsContext = createContext<[URLSearchParams, SetURLSearchParams] | null>(null);

export function useGlobalSearchParams() {
  const searchParams = useContext(GlobalSearchParamsContext);

  validateNotNullOrUndefined(
    searchParams,
    'useGlobalSearchParams must be used within GlobalSearchParamsProvider',
  );

  return searchParams;
}

type GlobalSearchParamsProviderProps = {
  useSearchParamsOptions?: URLSearchParamsInit;
};

export const GlobalSearchParamsProvider: React.FC<PropsWithChildren<GlobalSearchParamsProviderProps>> = ({
  children,
  useSearchParamsOptions,
}) => {
  const searchParams = useVySearchParams(useSearchParamsOptions);

  return (
    <GlobalSearchParamsContext.Provider value={searchParams}>{children}</GlobalSearchParamsContext.Provider>
  );
};
